import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Alert,
  Stack,
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  SwapHoriz as SwapHorizIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { DataService } from 'src/services/data-service';
import { ProductData } from 'src/types/Product-details.types';
import Loader from 'src/components/Loader';
import { LovService, LovDictionary } from 'src/services/lov-service';
import PredefinedVariableValueSelect from 'src/components/PredefinedVariableValueSelect';

interface UnitDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (unit: ProductData) => void;
  unit?: ProductData | null;
  mode?: 'create' | 'edit';
}

interface PropertyField {
  key: string;
  value: any;
  isNew?: boolean;
}

// Normalize additional properties into PropertyField[]
const toPropertyFields = (obj?: Record<string, any>): PropertyField[] =>
  obj
    ? Object.entries(obj).map(([key, value]) => ({ key, value, isNew: false }))
    : [];

const UnitDialog: React.FC<UnitDialogProps> = ({
  open,
  onClose,
  onSuccess,
  unit,
  mode = unit ? 'edit' : 'create',
}) => {
  const dispatch = useAppDispatch();

  // Form state (unit + item details together)
  const [formData, setFormData] = useState({
    id: '',
    barcode: '',
    symbol: '',
    unitOfMeasurement: '',
    itemName: '',
    itemMinNumber: 0,
    itemMaxNumber: 0,
    itemAdditionalProperties: {} as Record<string, any>,
  });
  const [properties, setProperties] = useState<PropertyField[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [originalBarcode, setOriginalBarcode] = useState('');
  const [searchingBarcode, setSearchingBarcode] = useState(false);
  const [barcodeExistsInInventory, setBarcodeExistsInInventory] = useState<{
    exists: boolean;
    unitData?: any;
  } | null>(null);
  const [showSwitchPrompt, setShowSwitchPrompt] = useState(false);
  const [predefinedValueOptions, setPredefinedValueOptions] = useState<LovDictionary>({});
  const [loadingLovs, setLoadingLovs] = useState(false);

  // Load predefined values
  useEffect(() => {
    if (open) {
      loadLovOptions();
    }
  }, [open]);

  const loadLovOptions = async () => {
    setLoadingLovs(true);
    try {
      const predefinedValues = await LovService.getLovDictionary('all-preDefinedValues');
      setPredefinedValueOptions(predefinedValues);
    } catch (error) {
      console.error('Failed to load LOV options:', error);
      dispatch(showAlert({ message: 'Failed to load form options', type: 'error' }));
    } finally {
      setLoadingLovs(false);
    }
  };

  // Initialize from props
  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && unit) {
      const barcode = unit.barcode || '';

      // Map properties back to predefined value IDs where possible
      const properties = unit.itemAdditionalProperties || unit.additionalProperties || {};
      const mappedProperties = Object.entries(properties).map(([name, value]) => {
        // Try to find the predefined value ID that matches this property name
        const predefinedValueId = Object.keys(predefinedValueOptions).find(
          id => predefinedValueOptions[id] === name
        );
        return {
          key: predefinedValueId || name,
          value: String(value),
          isNew: false
        };
      });

      setFormData({
        id: unit.id || '',
        barcode: barcode,
        symbol: unit.symbol || '',
        unitOfMeasurement: unit.unitOfMeasurement || '',
        itemName: unit.itemName || unit.name || '',
        itemMinNumber: unit.itemMinNumber || unit.minNumber || 0,
        itemMaxNumber: unit.itemMaxNumber || unit.maxNumber || 0,
        itemAdditionalProperties: properties,
      });
      setOriginalBarcode(barcode);
      setProperties(mappedProperties);
    } else {
      // create mode
      setFormData({
        id: '',
        barcode: '',
        symbol: '',
        unitOfMeasurement: '',
        itemName: '',
        itemMinNumber: 0,
        itemMaxNumber: 0,
        itemAdditionalProperties: {},
      });
      setOriginalBarcode('');
      setProperties([]);
    }
    setErrors({});
    setBarcodeExistsInInventory(null);
    setShowSwitchPrompt(false);
  }, [open, mode, unit, predefinedValueOptions]);

  // Keep itemAdditionalProperties in sync
  useEffect(() => {
    const itemAdditionalProperties: Record<string, any> = {};
    properties.forEach(p => {
      if (p.key && p.value !== null && p.value !== '') {
        // Use the predefined value name as the key, not the ID
        const predefinedValueName = predefinedValueOptions[p.key] || p.key;
        itemAdditionalProperties[predefinedValueName] = p.value;
      }
    });
    setFormData(prev => ({ ...prev, itemAdditionalProperties }));
  }, [properties, predefinedValueOptions]);

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addProperty = () => setProperties(prev => [...prev, { key: '', value: '', isNew: true }]);
  const updateProperty = (index: number, field: 'key' | 'value', newValue: string) =>
    setProperties(prev => prev.map((p, i) => (i === index ? { ...p, [field]: newValue } : p)));
  const removeProperty = (index: number) =>
    setProperties(prev => prev.filter((_, i) => i !== index));

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.barcode?.trim()) newErrors.barcode = 'Barcode is required';
    if (!formData.itemName?.trim()) newErrors.itemName = 'Item name is required';
    if (!formData.symbol?.trim()) newErrors.symbol = 'Symbol is required';
    if (!formData.unitOfMeasurement?.trim()) newErrors.unitOfMeasurement = 'Unit of measurement is required';

    properties.forEach((prop, index) => {
      if (prop.key && !prop.value) newErrors[`prop_${index}_value`] = 'Value is required';
      if (prop.value && !prop.key) newErrors[`prop_${index}_key`] = 'Property name is required';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const parseResponseSafely = async (response: Response) => {
    // Some APIs return 204, some return boolean true, some return { success, data, message }
    let body: any = null;
    try {
      // Attempt to parse JSON — may fail on 204 or empty body
      body = await response.json();
    } catch {
      // ignore parse error; treat as empty body
    }
    // Success heuristic:
    // - HTTP ok
    // - AND (no body) OR (boolean true) OR (object with success !== false)
    const succeeded =
      response.ok &&
      (body === null || body === true || (typeof body === 'object' && (body.success ?? true)));

    const message =
      typeof body === 'object' && body?.message
        ? body.message
        : response.ok
        ? undefined
        : 'Request failed';

    return { succeeded, body, message };
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        id: formData.id,
        barcode: formData.barcode,
        symbol: formData.symbol,
        unitOfMeasurement: formData.unitOfMeasurement,
        itemName: formData.itemName,
        itemMinNumber: formData.itemMinNumber,
        itemMaxNumber: formData.itemMaxNumber,
        itemAdditionalProperties: formData.itemAdditionalProperties,
      };

      const resp =
        mode === 'edit' && unit
          ? await DataService.put(`api/Unit/${formData.id}/with-item`, payload)
          : await DataService.post('api/Unit/create', payload);

      const { succeeded, body, message } = await parseResponseSafely(resp);
      if (!succeeded) throw new Error(message || `Failed to ${mode} unit`);

      // Build an updated ProductData to return to caller (optimistic update)
      const updated: ProductData = {
        ...(unit || ({} as ProductData)),
        id: formData.id || unit?.id || '',
        barcode: formData.barcode,
        symbol: formData.symbol,
        unitOfMeasurement: formData.unitOfMeasurement,
        name: formData.itemName,
        minNumber: formData.itemMinNumber,
        maxNumber: formData.itemMaxNumber,
        additionalProperties: { ...formData.itemAdditionalProperties },
        // touch updatedAt if present in the model
        updatedAt: new Date().toISOString(),
        // preserve the rest (isSynced, createdAt, etc.) from `unit`
      };

      dispatch(
        showAlert({
          message: `Unit ${mode === 'edit' ? 'updated' : 'created'} successfully`,
          type: 'success',
        })
      );

      onSuccess?.(updated);
      onClose();
    } catch (error: any) {
      dispatch(
        showAlert({
          message: error?.message || `Failed to ${mode} unit`,
          type: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {mode === 'edit' ? 'Edit Unit' : 'Create New Unit'}
          </Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <Loader message={`${mode === 'edit' ? 'Updating' : 'Creating'} unit...`} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Barcode *"
                value={formData.barcode}
                onChange={(e) => handleInputChange('barcode', e.target.value)}
                error={!!errors.barcode}
                helperText={errors.barcode}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Name *"
                value={formData.itemName}
                onChange={(e) => handleInputChange('itemName', e.target.value)}
                error={!!errors.itemName}
                helperText={errors.itemName}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Symbol *"
                value={formData.symbol}
                onChange={(e) => handleInputChange('symbol', e.target.value)}
                error={!!errors.symbol}
                helperText={errors.symbol}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit *"
                value={formData.unitOfMeasurement}
                onChange={(e) => handleInputChange('unitOfMeasurement', e.target.value)}
                error={!!errors.unitOfMeasurement}
                helperText={errors.unitOfMeasurement}
                variant="outlined"
              />
            </Grid>

            {/* Number Range */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Order Numbers
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Min Number"
                type="number"
                value={formData.itemMinNumber}
                onChange={(e) =>
                  handleInputChange('itemMinNumber', e.target.value ? Number(e.target.value) : 0)
                }
                variant="outlined"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Max Number"
                type="number"
                value={formData.itemMaxNumber}
                onChange={(e) =>
                  handleInputChange('itemMaxNumber', e.target.value ? Number(e.target.value) : 0)
                }
                variant="outlined"
              />
            </Grid>

            {/* Additional Properties */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Additional Properties
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addProperty} variant="outlined">
                  Add Property
                </Button>
              </Box>

              {properties.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon fontSize="small" />
                    <Typography variant="body2">
                      No additional properties defined. Click "Add Property" to add custom attributes
                      like size, color, brand, etc.
                    </Typography>
                  </Box>
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {properties.map((property, index) => (
                    <Card key={index} variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Property</InputLabel>
                            <Select
                              value={property.key}
                              onChange={(e) => updateProperty(index, 'key', e.target.value)}
                              label="Property"
                              disabled={loadingLovs}
                              error={!!errors[`prop_${index}_key`]}
                            >
                              <MenuItem value="">
                                <em>Select Property</em>
                              </MenuItem>
                              {Object.entries(predefinedValueOptions).map(([id, name]) => (
                                <MenuItem key={id} value={id}>
                                  {name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={5}>
                          <PredefinedVariableValueSelect
                            predefinedVariableId={property.key}
                            value={property.value}
                            onChange={(newValue) => updateProperty(index, 'value', newValue)}
                            disabled={loadingLovs || !property.key}
                            label="Value"
                            size="small"
                            error={!!errors[`prop_${index}_value`]}
                            helperText={errors[`prop_${index}_value`]}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton onClick={() => removeProperty(index)} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={<SaveIcon />}>
          {mode === 'edit' ? 'Update' : 'Create'} Unit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnitDialog;